class MeriphoraAnnouncements extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
    this.index = 0;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.motion.matches;
    this.hovered = false;
    this.focusPaused = false;
    this.gesture = null;
    this.suppressClickUntil = 0;
    this.toggleAttribute('data-enhanced', true);
    this.toggleAttribute('data-multiple', this.slides.length > 1);
    this.addEventListener('mouseenter', () => { this.hovered = true; this.refreshPlayback(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.refreshPlayback(); }, { signal });
    this.addEventListener('focusin', () => { this.focusPaused = true; this.refreshPlayback(); }, { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.isConnected) return;
      this.focusPaused = this.contains(document.activeElement);
      this.refreshPlayback();
    }), { signal });
    this.addEventListener('pointerdown', event => this.startGesture(event), { signal });
    this.addEventListener('pointermove', event => this.moveGesture(event), { signal, passive: false });
    this.addEventListener('pointerup', event => this.finishGesture(event, true), { signal });
    this.addEventListener('pointercancel', event => this.finishGesture(event, false), { signal });
    this.addEventListener('lostpointercapture', event => {
      if (event.target === this) this.finishGesture(event, false);
    }, { signal });
    this.addEventListener('dragstart', event => { if (this.slides.length > 1) event.preventDefault(); }, { signal });
    this.addEventListener('click', event => {
      if (event.detail === 0 || performance.now() >= this.suppressClickUntil) return;
      this.suppressClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { signal, capture: true });
    this.addEventListener('keydown', event => {
      if (event.key === ' ' && event.target === this && this.dataset.autoplay === 'true') {
        event.preventDefault();
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.focusPaused = false;
        this.refreshPlayback();
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? -1 : 1;
      this.slideTo(this.index - direction, direction, 0, true);
    }, { signal });
    document.addEventListener('visibilitychange', () => this.refreshPlayback(), { signal });
    document.addEventListener('shopify:block:select', event => {
      const slide = event.target instanceof Element ? event.target.closest('[data-announcement-slide]') : null;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.userPaused = true;
      this.select(index);
      this.refreshPlayback();
    }, { signal });
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) {
        this.userPaused = true;
        this.stopSlideMotion();
      }
      this.refreshPlayback();
    }, { signal });
    window.addEventListener('pageshow', () => this.updateRewardMinimum(), { signal });
    this.updateRewardMinimum();
    this.select(0);
    this.refreshPlayback();
  }

  updateRewardMinimum() {
    const currency = window.Shopify?.currency;
    const rate = Number(currency?.rate);
    const local = currency?.active === this.dataset.currency && this.dataset.currency !== 'USD'
      && Number.isFinite(rate) && rate > 0;
    for (const amount of this.querySelectorAll('[data-reward-minimum]')) {
      const base = Number(amount.dataset.rewardMinimum || this.dataset.baseMinimum);
      const prefix = amount.parentElement.querySelector('[data-reward-prefix]');
      amount.dataset.originalText ??= amount.textContent;
      if (prefix) prefix.dataset.originalText ??= prefix.textContent;
      if (!local || !Number.isFinite(base) || base <= 0) {
        amount.textContent = amount.dataset.originalText;
        if (prefix) prefix.textContent = prefix.dataset.originalText;
        continue;
      }
      const minimum = Math.ceil(Math.ceil(base * 100 * rate - 0.000001) / 100);
      amount.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency, maximumFractionDigits: 0,
      }).format(minimum) + '+';
      if (prefix) prefix.textContent = this.dataset.localRewardPrefix;
    }
  }

  disconnectedCallback() {
    this.finishGesture(null, false);
    this.stopSlideMotion();
    clearInterval(this.timer);
    this.timer = undefined;
    this.controller?.abort();
    this.controller = null;
  }

  startGesture(event) {
    if (this.slides.length < 2 || this.gesture || !event.isPrimary || event.button !== 0) return;
    this.stopSlideMotion();
    this.suppressClickUntil = 0;
    this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, horizontal: false };
    this.refreshPlayback();
  }

  moveGesture(event) {
    const gesture = this.gesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        this.finishGesture(event, false);
        return;
      }
      gesture.horizontal = true;
      this.setAttribute('data-dragging', '');
      try { this.setPointerCapture(event.pointerId); } catch { /* The pointer may already have been cancelled. */ }
    }
    if (event.cancelable) event.preventDefault();
    gesture.dx = dx;
    const limit = this.clientWidth;
    this.style.setProperty('--announcement-drag-offset', `${Math.max(-limit, Math.min(limit, dx))}px`);
  }

  finishGesture(event, allowSwitch) {
    const gesture = this.gesture;
    if (!gesture || (event && event.pointerId !== gesture.id)) return;
    this.gesture = null;
    this.removeAttribute('data-dragging');
    this.style.removeProperty('--announcement-drag-offset');
    if (this.hasPointerCapture(gesture.id)) this.releasePointerCapture(gesture.id);
    const threshold = Math.min(36, this.clientWidth * 0.12);
    if (allowSwitch && gesture.horizontal) {
      // A drag must never activate an announcement's optional link.
      this.suppressClickUntil = performance.now() + 400;
      if (Math.abs(gesture.dx) >= threshold) {
        const direction = gesture.dx < 0 ? -1 : 1;
        const offset = Math.max(-this.clientWidth, Math.min(this.clientWidth, gesture.dx));
        this.slideTo(this.index - direction, direction, offset, true);
      }
    }
    this.refreshPlayback();
  }

  select(index, announce = false) {
    if (!this.slides.length) return;
    this.stopSlideMotion();
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, position) => {
      const active = position === this.index;
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    if (announce) {
      this.querySelector('[data-announcement-status]').textContent =
        this.dataset.position.replace('__CURRENT__', String(this.index + 1)) + ': ' + this.slides[this.index].textContent.trim();
    }
  }

  stopSlideMotion() {
    this.motionSequence = (this.motionSequence || 0) + 1;
    for (const animation of this.slideAnimations || []) animation.cancel();
    this.slideAnimations = [];
    this.animating = false;
    for (const slide of this.slides || []) slide.removeAttribute('data-leaving');
  }

  slideTo(index, direction, offset = 0, announce = false) {
    if (!this.slides.length) return;
    const outgoing = this.slides[this.index];
    this.select(index, announce);
    const incoming = this.slides[this.index];
    if (outgoing === incoming || this.motion.matches || !incoming.animate) return;
    const width = this.clientWidth;
    if (!width) return;
    const sequence = this.motionSequence;
    outgoing.setAttribute('data-leaving', '');
    this.animating = true;
    const timing = { duration: 280, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'both' };
    // Keep the outgoing message painted until it travels fully beyond the clipped bar.
    this.slideAnimations = [
      outgoing.animate([
        { transform: `translateX(${offset}px)` }, { transform: `translateX(${direction * width}px)` },
      ], timing),
      incoming.animate([
        { transform: `translateX(${-direction * width}px)` }, { transform: 'translateX(0px)' },
      ], timing),
    ];
    this.refreshPlayback();
    Promise.allSettled(this.slideAnimations.map(animation => animation.finished)).then(() => {
      if (sequence !== this.motionSequence) return;
      this.stopSlideMotion();
      this.refreshPlayback();
    });
  }

  refreshPlayback() {
    clearInterval(this.timer);
    this.timer = undefined;
    if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || this.userPaused || this.hovered || this.focusPaused || this.gesture || this.animating || document.hidden) return;
    const seconds = Math.max(3, Number(this.dataset.interval) || 5);
    this.timer = setInterval(() => this.slideTo(this.index + 1, -1), seconds * 1000);
  }
}

if (!customElements.get('meriphora-announcements')) customElements.define('meriphora-announcements', MeriphoraAnnouncements);
